"use client";

import { ArrowLeft, Loader2 } from "lucide-react";
import React, { useRef, useState, useEffect } from "react";
import { cn } from "@punchup/utils";

import { UnderlineTabs } from "../../common/UnderlineTabs";
import { BuyCreditsTab } from "./BuyCreditsTab";
import { DetailsTab } from "./DetailsTab";
import { HistoryTab } from "./HistoryTab";
import type {
  CreditBalanceModalProps,
  CreditTier,
  PurchaseMode,
} from "./types";

type CreditTab = "details" | "history" | "buy-credits";

const CREDIT_TABS = [
  { value: "buy-credits", label: "Buy Credits" },
  { value: "details", label: "Details" },
  { value: "history", label: "History" },
] as const;

export interface InlineCreditBalanceContentProps {
  balance: CreditBalanceModalProps["balance"];
  isLoading: boolean;
  initialTab?: CreditTab;
  onFetchCheckoutSession: CreditBalanceModalProps["onFetchCheckoutSession"];
  onFetchTransactions: CreditBalanceModalProps["onFetchTransactions"];
  onPaymentComplete: () => void;
  userEmail?: string;
  activeSubscription?: CreditBalanceModalProps["activeSubscription"];
  onCancelSubscription?: CreditBalanceModalProps["onCancelSubscription"];
  onUpdatePaymentMethod?: CreditBalanceModalProps["onUpdatePaymentMethod"];
  onChangePlan?: () => void;
  onChangeTier?: CreditBalanceModalProps["onChangeTier"];
  onPreviewTierChange?: CreditBalanceModalProps["onPreviewTierChange"];
  /** Callback to return to the messaging flow */
  onBack: () => void;
  /** Optional override: fires after payment, then returns to flow */
  onPaymentCompleteAndReturn?: () => void;
  /** Resolved `updated_credits_pricing` gate value. Defaults to false. */
  updatedPricing?: boolean;
}

export function InlineCreditBalanceContent({
  balance,
  isLoading,
  initialTab = "buy-credits",
  onFetchCheckoutSession,
  onFetchTransactions,
  onPaymentComplete,
  userEmail,
  activeSubscription,
  onCancelSubscription,
  onUpdatePaymentMethod,
  onChangePlan,
  onChangeTier,
  onPreviewTierChange,
  onBack,
  onPaymentCompleteAndReturn,
  updatedPricing = false,
}: InlineCreditBalanceContentProps) {
  const [activeTab, setActiveTab] = useState<CreditTab>(initialTab);
  const [buyCreditsInitialSelection, setBuyCreditsInitialSelection] = useState<{
    tier: CreditTier;
    mode: PurchaseMode;
  } | null>(null);
  const mobileSlideRef = useRef<HTMLDivElement>(null);
  const footerPortalRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef<CreditTab>(activeTab);

  // Sync initialTab on mount
  useEffect(() => {
    setActiveTab(initialTab);
    setBuyCreditsInitialSelection(null);
  }, [initialTab]);

  const handlePaymentCompleteFinal = () => {
    if (onPaymentCompleteAndReturn) {
      onPaymentCompleteAndReturn();
    } else {
      onPaymentComplete();
    }
  };

  const handleChangePlan = () => {
    const oppositeTier = activeSubscription?.tier === "pro" ? "starter" : "pro";
    setBuyCreditsInitialSelection({
      tier: oppositeTier,
      mode: "subscription",
    });
    setActiveTab("buy-credits");
    onChangePlan?.();
  };

  const handleRefill = () => {
    setBuyCreditsInitialSelection({
      tier: "starter",
      mode: "one_time",
    });
    setActiveTab("buy-credits");
  };

  const loadingSpinner = (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  const buyCreditsContent = (
    <BuyCreditsTab
      onFetchCheckoutSession={onFetchCheckoutSession}
      onPaymentComplete={handlePaymentCompleteFinal}
      userEmail={userEmail}
      activeSubscription={activeSubscription}
      onChangeTier={onChangeTier}
      onPreviewTierChange={onPreviewTierChange}
      initialSelection={buyCreditsInitialSelection}
      isDesktop={false}
      balance={balance}
      footerPortalRef={footerPortalRef}
      updatedPricing={updatedPricing}
    />
  );

  const detailsContent = (
    <DetailsTab
      balance={balance}
      activeSubscription={activeSubscription}
      onCancelSubscription={onCancelSubscription}
      onUpdatePaymentMethod={onUpdatePaymentMethod}
      onChangePlan={handleChangePlan}
      onRefill={handleRefill}
      isDesktop={false}
    />
  );

  const historyContent = (
    <HistoryTab
      onFetchTransactions={onFetchTransactions}
      isDesktop={false}
      onSwitchTab={(tab: string) => setActiveTab(tab as CreditTab)}
    />
  );

  const handleMobileTabChange = (value: string) => {
    const newTab = value as CreditTab;
    if (newTab === activeTab) return;

    const tabOrder: CreditTab[] = ["buy-credits", "details", "history"];
    const oldIndex = tabOrder.indexOf(prevTabRef.current);
    const newIndex = tabOrder.indexOf(newTab);
    const direction = newIndex > oldIndex ? "right" : "left";

    prevTabRef.current = newTab;
    setActiveTab(newTab);

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const el = mobileSlideRef.current;
    if (el && !prefersReduced) {
      el.style.transition = "none";
      el.style.opacity = "0";
      el.style.transform =
        direction === "right" ? "translateX(8%)" : "translateX(-8%)";
      el.getBoundingClientRect();
      el.style.transition =
        "transform 220ms cubic-bezier(0.25, 1, 0.5, 1), opacity 220ms ease-out";
      el.style.opacity = "1";
      el.style.transform = "translateX(0)";
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Back header */}
      <div className="flex items-center gap-2 px-4 pt-2 pb-1">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"
          aria-label="Back to messaging"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-base font-semibold text-[#29333C]">Credits</h2>
      </div>

      <UnderlineTabs
        tabs={[...CREDIT_TABS]}
        activeTab={activeTab}
        onTabChange={handleMobileTabChange}
        className="px-4 shrink-0"
      />

      <div className="flex-1 overflow-auto p-4 min-h-0">
        <div ref={mobileSlideRef}>
          {isLoading
            ? loadingSpinner
            : activeTab === "buy-credits"
              ? buyCreditsContent
              : activeTab === "details"
                ? detailsContent
                : historyContent}
        </div>
      </div>
      <div ref={footerPortalRef} className="shrink-0" />
    </div>
  );
}
